import React, { useEffect, useState } from "react";
import SelectTemplateSVG from "public/assets/heroStepsIllustrations/selectTemplateSVG.svg";
import ProvideDetailsSVG from "public/assets/heroStepsIllustrations/provideDetailsSVG.svg";
import UploadResumeSVG from "public/assets/heroStepsIllustrations/uploadResumeSVG.svg";
import EasyDownloadSVG from "public/assets/heroStepsIllustrations/easyDownloadSVG.svg";
import WavyLineSVG from "public/assets/heroStepsIllustrations/wavyLineSVG.svg";
import { useIntersectionObserver } from "lib/hooks/useIntersectionObserver";

export const Steps = () => {
  return (
    <section className="bg-white">
      <div className="text-center font-montserrat tracking-tight text-[#2B282B]">
        <div className="text-[18px] font-medium md:text-[20.4px] lg:text-[27.2px] xl:text-[34px] 2xl:text-[40.8px]">
          Elevate Your <span className="text-[#52A1FF]">Career</span>
        </div>
        <p className="text-[10px] font-normal md:text-[12.4px] lg:text-[19.2px] xl:text-[22px] 2xl:text-[28.8px]">
          With Our User friendly Resume Builder
        </p>
      </div>
      <div className="relative mt-2 flex flex-col gap-20">
        <WavyLineSVG className="absolute left-1/2 hidden -translate-x-[20%] md:block" />
        <div className="mx-auto flex w-[90%] items-center justify-between text-[#3C3C3C] md:w-[57%]">
          <AnimatedElement slideFrom="left">
            <div className="steps-shadow rounded-md p-2 text-left md:px-6 md:py-4">
              <h3 className="text-xs font-semibold lg:text-base xl:text-xl 2xl:text-2xl">
                Select Your Ideal Template
              </h3>
              <p className="mt-2 text-[9px] font-normal md:mt-4 md:text-[9.6px] lg:text-[12.8px] xl:text-base 2xl:text-xl">
                Browse our professionally crafted <br />
                templates designed to pass ATS <br />
                filters and stand out.
              </p>
            </div>
          </AnimatedElement>
          <AnimatedElement slideFrom="right">
            <SelectTemplateSVG className="h-[93px] w-[103px] md:h-[140px] md:w-[164px] lg:h-[187px] lg:w-[218px] xl:h-[233px] xl:w-[273px] 2xl:h-[279px] 2xl:w-[328px]" />
          </AnimatedElement>
        </div>
        <div className="mx-auto flex w-[90%] items-center justify-between text-[#3C3C3C] md:w-[57%]">
          <AnimatedElement slideFrom="left">
            <ProvideDetailsSVG className="h-[88px] w-[103px] md:h-[140px] md:w-[164px] lg:h-[187px] lg:w-[218px] xl:h-[233px] xl:w-[273px] 2xl:h-[279px] 2xl:w-[328px]" />
          </AnimatedElement>
          <AnimatedElement slideFrom="right">
            <div className="steps-shadow rounded-md p-2 text-left md:px-6 md:py-4">
              <h3 className="text-xs font-semibold lg:text-base xl:text-xl 2xl:text-2xl">
                Provide Your Details
              </h3>
              <p className="mt-2 text-[9px] font-normal md:mt-4 md:text-[9.6px] lg:text-[12.8px] xl:text-base 2xl:text-xl">
                Add your career data step-
                <br />
                by-step for a resume that <br />
                reflects your unique profile
              </p>
            </div>
          </AnimatedElement>
        </div>
        <div className="mx-auto flex w-[90%] items-center justify-between text-[#3C3C3C] md:w-[57%]">
          <AnimatedElement slideFrom="left">
            <div className="steps-shadow rounded-md p-2 text-left md:px-6 md:py-4">
              <h3 className="text-xs font-semibold lg:text-base xl:text-xl 2xl:text-2xl">
                Select Your Ideal Template
              </h3>
              <p className="mt-2 text-[9px] font-normal md:mt-4 md:text-[9.6px] lg:text-[12.8px] xl:text-base 2xl:text-xl">
                Let our AI scan and auto-fill your <br /> resume information to
                save you <br />
                time. <br />
              </p>
            </div>
          </AnimatedElement>
          <AnimatedElement slideFrom="right">
            <UploadResumeSVG className="h-[87px] w-[103px] md:h-[140px] md:w-[164px] lg:h-[187px] lg:w-[218px] xl:h-[233px] xl:w-[273px] 2xl:h-[279px] 2xl:w-[328px]" />
          </AnimatedElement>
        </div>
        <div className="mx-auto flex w-[90%] justify-center gap-2 text-[#3C3C3C] md:w-[57%] md:gap-16">
          <AnimatedElement slideFrom="left">
            <div className="steps-shadow h-fit rounded-md p-2 text-left md:px-6 md:py-4">
              <h3 className="text-xs lg:text-base xl:text-xl 2xl:text-2xl">
                Easily <span className="font-semibold">download</span>
                <br />
                your new resume
                <br />
                and <span className="font-semibold">save</span> your work
              </h3>
            </div>
          </AnimatedElement>
          <AnimatedElement slideFrom="right">
            <EasyDownloadSVG className="mt-5 h-[73px] w-[86px] md:h-[82px] md:w-[97px] lg:h-[109px] lg:w-[129px] xl:h-[136px] xl:w-[162px] 2xl:h-[163px] 2xl:w-[194px]" />
          </AnimatedElement>
        </div>
      </div>
    </section>
  );
};

type SlideDirection = "left" | "right";
interface AnimatedProps {
  children: React.ReactNode;
  slideFrom: SlideDirection;
  className?: string;
}

export const AnimatedElement = ({
  children,
  slideFrom,
  className,
}: AnimatedProps) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, ref] = useIntersectionObserver<HTMLDivElement>(0.3);

  useEffect(() => {
    if (isVisible && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isVisible]);

  const slideClass =
    slideFrom === "left"
      ? `${
          hasAnimated ? "translate-x-0" : "-translate-x-20 md:-translate-x-full"
        }`
      : `${
          hasAnimated ? "translate-x-0" : "translate-x-20 md:translate-x-full"
        }`;

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${slideClass} ${
        hasAnimated ? "opacity-100" : "opacity-0"
      } ${className}`}
    >
      {children}
    </div>
  );
};
